import { useRef, useState } from 'react';
import { Paperclip, Trash2, Download, Link2, Check, Loader2, Plus } from 'lucide-react';
import { api } from '../lib/api.js';
import { attachmentUrl, uploadFile, formatBytes } from '../lib/upload.js';

/**
 * The evidence filed against one ticket.
 *
 * Uploads here use the `ticket` context, so the object is keyed under the app
 * rather than a conversation — a ticket outlives the thread it came from, and
 * may never have had one.
 *
 * Removing unfiles the attachment; it does not delete the object, which may
 * still be on the message it arrived on.
 */
export function TicketAttachments({ appId, ticket, uploads, onChange, canEdit = true }) {
  const [busy, setBusy] = useState(false);
  const [pending, setPending] = useState([]);
  const [copied, setCopied] = useState(null);
  const [error, setError] = useState(null);
  const picker = useRef(null);

  const files = ticket.attachments ?? [];

  const add = async (picked) => {
    const list = Array.from(picked ?? []).filter(Boolean);
    if (!list.length) return;
    setError(null);
    setPending(list.map((f) => f.name));

    try {
      const uploaded = [];
      for (const file of list) {
        if (uploads.maxBytes && file.size > uploads.maxBytes) {
          throw new Error(`${file.name} is larger than ${formatBytes(uploads.maxBytes)}`);
        }
        uploaded.push(await uploadFile(appId, file, { context: 'ticket' }));
      }
      onChange(
        await api.tickets.attach(
          appId,
          ticket.id,
          uploaded.map(({ key, name, mimeType, size }) => ({ key, name, mimeType, size }))
        )
      );
    } catch (err) {
      setError(err.message);
    } finally {
      setPending([]);
    }
  };

  const detach = async (file) => {
    setBusy(true);
    setError(null);
    try {
      onChange(await api.tickets.detach(appId, ticket.id, file.key));
    } catch (err) {
      setError(err.message);
    } finally {
      setBusy(false);
    }
  };

  const open = async (file, options) => {
    setError(null);
    try {
      window.open(await attachmentUrl(appId, file, options), '_blank', 'noopener,noreferrer');
    } catch (err) {
      setError(err.message);
    }
  };

  const copy = async (file) => {
    setError(null);
    try {
      await navigator.clipboard.writeText(await attachmentUrl(appId, file));
      setCopied(file.key);
      setTimeout(() => setCopied(null), 2000);
    } catch (err) {
      setError(err.message);
    }
  };

  return (
    <div className="ticket-files">
      {files.length === 0 && pending.length === 0 && (
        <span className="ticket-files__empty">No files on this ticket.</span>
      )}

      {files.map((file) => (
        <div className="ticket-file" key={file.key}>
          <Paperclip size={11} />
          <button type="button" className="ticket-file__name" onClick={() => open(file)}>
            {file.name || 'Attachment'}
          </button>
          {file.size > 0 && <span className="file__size">{formatBytes(file.size)}</span>}
          <div className="file__actions file__actions--static">
            <button
              type="button"
              className="icon-button"
              onClick={() => copy(file)}
              title="Copy a shareable link"
            >
              {copied === file.key ? <Check size={12} /> : <Link2 size={12} />}
            </button>
            <button
              type="button"
              className="icon-button"
              onClick={() => open(file, { download: true })}
              title="Download"
            >
              <Download size={12} />
            </button>
            {canEdit && (
              <button
                type="button"
                className="icon-button"
                onClick={() => detach(file)}
                disabled={busy}
                title="Remove from this ticket (the file itself is kept)"
              >
                <Trash2 size={12} />
              </button>
            )}
          </div>
        </div>
      ))}

      {pending.map((name) => (
        <div className="ticket-file" key={`pending-${name}`}>
          <Loader2 size={11} className="spin" />
          <span className="ticket-file__name">{name}</span>
        </div>
      ))}

      {canEdit && uploads.enabled && (
        <>
          <input
            ref={picker}
            type="file"
            multiple
            hidden
            onChange={(e) => {
              add(e.target.files);
              e.target.value = '';
            }}
          />
          <button
            type="button"
            className="macro"
            onClick={() => picker.current?.click()}
            disabled={pending.length > 0}
          >
            <Plus size={11} /> Attach a file
          </button>
        </>
      )}

      {error && <div className="login__error">{error}</div>}
    </div>
  );
}

export default TicketAttachments;
